// Секция «Радар объёмов»: grafic mare + panoul cu monedele în care intră bani.
//
// Trei file peste aceeași listă:
//   Приток — oborotul crește față de normalul recent (×N);
//   Сделки — *cine* aduce oborotul, nu doar cât;
//   Склад  — oborot mare, dar preț care stă pe loc (acumulare).
//
// Fila „Сделки" e singura care spune ceva despre ceilalți participanți, și o face
// dintr-o comparație simplă: oborotul și numărul de tranzacții au fiecare propriul
// multiplicator față de aceeași bază. Dacă banii cresc mai repede decât numărul de
// tranzacții, fiecare tranzacție e mai mare decât de obicei — intră cineva mare. Dacă
// tranzacțiile cresc mai repede decât banii, sunt mulți ordini mărunți — mulțimea.
// Raportul dintre cei doi multiplicatori e chiar de câte ori s-a îngroșat tranzacția
// medie, deci ×2.0 la „Крупный игрок" înseamnă tranzacții de două ori mai mari ca de
// obicei — nu o notă inventată.
//
// Numărul de tranzacții per bară vine doar din klines Binance. Unde lipsește, moneda
// nu apare în „Сделки": mai bine mai puține monede decât o etichetă ghicită.
//
// Radarul lucrează implicit doar pe cripto. Binance listează 177 de contracte TradFi
// (acțiuni, mărfuri, indici tokenizați) lângă cele ~565 de monede, iar ele dorm cât e
// bursa închisă și pornesc la deschidere. Un radar de volum le scoate atunci primele,
// cu multiplicatori de ×80 care nu înseamnă nimic pentru un scalper de cripto — 12 din
// primele 20 de poziții erau acțiuni. Cine le vrea le aprinde din setări.
//
// Tot de aici iese și filtrul de cotare: Binance are pentru aceleași active și perpetue
// în USDC sau USD1. Fără filtru, TRUMP apărea de două ori în aceeași listă — o dată
// ×25.6 și o dată ×17.7 —, ceea ce nu adaugă nicio informație, doar ocupă un card.

import { el, clear, bus, debounce, fmtVolume, fmtPct, splitSymbol } from '../core/util.js';
import { iconMoneda } from '../ui/coin-icon.js';
import { icon } from '../ui/icons.js';
import { settings, market } from '../core/state.js';
import { ChartPanel } from '../ui/chart-panel.js';
import { candleSparkline, baseTicker, modal, toggle } from '../ui/components.js';

const TABS = [
  { id: 'flow', label: 'Приток', icon: 'radar' },
  { id: 'trades', label: 'Сделки', icon: 'fa-arrows-up-down' },
  { id: 'storage', label: 'Склад', icon: 'fa-layer-group' },
];

/** Cele două fețe ale filei „Сделки". */
const REGIMURI = [
  { id: 'big', label: 'Крупный игрок' },
  { id: 'crowd', label: 'Толпа' },
];

/** Antetul se schimbă odată cu fila: titlul spune ce vezi, subtitlul de ce. */
const ANTET = {
  flow: {
    icon: 'radar',
    title: 'Приток объёма',
    sub: 'монеты с растущим оборотом · обновляется автоматически',
  },
  big: {
    icon: 'fa-paper-plane',
    title: 'Крупный игрок',
    sub: 'приток идёт крупными заявками — оборот растёт быстрее числа сделок',
  },
  crowd: {
    icon: 'fa-users',
    title: 'Толпа мелких ордеров',
    sub: 'приток идёт мелкими ордерами — сделок прибавляется быстрее оборота',
  },
  storage: {
    icon: 'fa-layer-group',
    title: 'Склад',
    sub: 'оборот есть, а цена стоит на месте — похоже на набор позиции',
  },
};

/** Textul din spatele semnului „?" de pe card. */
const LAMURIRI = {
  big: 'Оборот вырос сильнее, чем число сделок: средняя сделка стала крупнее обычной. '
    + 'Множитель показывает, во сколько раз.',
  crowd: 'Сделок стало больше, чем прибавилось оборота: средняя сделка мельче обычной. '
    + 'Множитель показывает, во сколько раз.',
};

const DEFAULTS = {
  minMultiplier: 1.5,   // ×N față de normalul recent
  minVolume: 1_000_000, // oborot minim 24h
  maxChange: 3,         // „Склад": mișcare de preț sub N%
  minRatio: 1.1,        // „Сделки": cât de mult trebuie să încline balanța
  limit: 60,
  tradfi: false,        // acțiuni/mărfuri tokenizate — implicit în afara radarului
};

export class VolumeRadarSection {
  constructor(app) {
    this.app = app;
    this.tab = settings.get('sx_volradar_flow', 'flow');
    this.tradeMode = settings.get('sx_volradar_trade_mode', 'big');
    this.filters = { ...DEFAULTS, ...(settings.get('sx_volradar_filters', {}) || {}) };
    this.unsubs = [];
    this.cardEls = new Map();
    this.activ = settings.get('sx_coins_symbol', 'BTCUSDT');
  }

  mount(host) {
    this.root = el('div', { class: 'radar-section' });
    if (settings.get('sx_volradar_collapsed', false)) this.root.classList.add('collapsed');
    this.chartHost = el('div', { class: 'radar-chart' });
    this.panelEl = this.#panel();
    this.root.append(this.chartHost, this.panelEl);
    host.append(this.root);

    this.panel = new ChartPanel({
      exchange: this.app.exchange,
      market: this.app.market,
      symbol: settings.get('sx_coins_symbol', 'BTCUSDT'),
      tf: '5m',
      // Moneda se poate schimba şi din căutarea graficului, nu doar dintr-un card —
      // fără asta, evidenţierea ar rămâne pe cardul vechi.
      onSymbolChange: (sym) => { this.activ = sym; this.#syncActive(); },
    }).mount(this.chartHost);

    this.unsubs.push(bus.on('market:aggregates', debounce(() => this.#renderCards(), 400)));
    this.unsubs.push(bus.on('market:sparklines', () => this.#renderCards()));
    // Culorile lumânărilor de pe carduri se schimbă odată cu tema, ca peste tot.
    this.unsubs.push(bus.on('theme:change', () => this.#renderCards()));
    this.#syncHead();
    this.#renderCards();
  }

  unmount() {
    this.unsubs.forEach((fn) => fn());
    this.unsubs = [];
    this.panel?.destroy();
    this.root?.remove();
  }

  /** Regimul curent — cheia din `ANTET` și criteriul de sortare. */
  get mode() {
    return this.tab === 'trades' ? this.tradeMode : this.tab;
  }

  #panel() {
    this.tabsEl = el('div', { class: 'radar-tabs' }, TABS.map((tab) => el('button', {
      class: `radar-tab${tab.id === this.tab ? ' active' : ''}`,
      onclick: () => {
        this.tab = tab.id;
        settings.set('sx_volradar_flow', tab.id);
        [...this.tabsEl.children].forEach((c) => c.classList.toggle('active', c.dataset.id === tab.id));
        this.#syncHead();
        this.#renderCards();
      },
      dataset: { id: tab.id },
    }, [icon(tab.icon, 13), el('span', { text: tab.label })])));

    this.modesEl = el('div', { class: 'radar-modes' }, REGIMURI.map((m) => el('button', {
      class: `radar-mode${m.id === this.tradeMode ? ' active' : ''}`,
      text: m.label,
      dataset: { id: m.id },
      onclick: () => {
        this.tradeMode = m.id;
        settings.set('sx_volradar_trade_mode', m.id);
        [...this.modesEl.children].forEach((c) => c.classList.toggle('active', c.dataset.id === m.id));
        this.#syncHead();
        this.#renderCards();
      },
    })));

    this.headIcon = el('span', { class: 'radar-head-ico' });
    this.titleEl = el('span', { class: 'radar-title' });
    this.subEl = el('div', { class: 'radar-sub' });
    this.metaEl = el('div', { class: 'radar-meta' });
    this.cardsEl = el('div', { class: 'radar-cards' });

    return el('div', { class: 'radar-panel' }, [
      el('div', { class: 'radar-head' }, [
        this.headIcon,
        this.titleEl,
        el('button', {
          class: 'radar-gear', title: 'Только закладки',
          onclick: (e) => {
            this.onlyMarks = !this.onlyMarks;
            e.currentTarget.classList.toggle('on', this.onlyMarks);
            this.#renderCards();
          },
        }, [icon('bookmark', 14)]),
        el('button', {
          class: 'radar-gear', title: 'Настройки радара',
          onclick: () => this.#openSettings(),
        }, [icon('gear', 15)]),
      ]),
      this.subEl,
      this.tabsEl,
      this.modesEl,
      this.metaEl,
      this.cardsEl,
      el('button', {
        class: 'radar-collapse',
        onclick: () => {
          const ascuns = this.root.classList.toggle('collapsed');
          settings.set('sx_volradar_collapsed', ascuns);
          setTimeout(() => this.panel?.chart?.resize(), 220);
        },
      }, [icon('chevronRight', 12), el('span', { text: 'Скрыть список' })]),
    ]);
  }

  /** Titlul, subtitlul și rândul de regimuri, potrivite cu fila activă. */
  #syncHead() {
    const a = ANTET[this.mode];
    clear(this.headIcon);
    this.headIcon.append(icon(a.icon, 16));
    this.titleEl.textContent = a.title;
    this.subEl.textContent = a.sub;
    this.modesEl.style.display = this.tab === 'trades' ? '' : 'none';
  }

  #openSettings() {
    const draft = { ...this.filters };
    const numberRow = (label, key, step = 1) => el('label', { class: 'radar-set-row' }, [
      el('span', { text: label }),
      el('input', {
        type: 'number', class: 'sfm-input sfm-input-sm', value: draft[key], step,
        oninput: (e) => { draft[key] = Number(e.target.value) || 0; },
      }),
    ]);

    modal({
      title: 'Настройки радара',
      width: 440,
      body: el('div', { class: 'radar-settings' }, [
        numberRow('Минимальный множитель (×)', 'minMultiplier', 0.1),
        numberRow('Минимальный оборот 24ч ($)', 'minVolume', 100000),
        numberRow('«Склад»: движение цены не больше (%)', 'maxChange', 0.5),
        numberRow('«Сделки»: перекос не меньше (×)', 'minRatio', 0.05),
        numberRow('Сколько монет показывать', 'limit', 5),
        toggle(draft.tradfi, (v) => { draft.tradfi = v; }, {
          title: 'Акции и товары',
          subtitle: 'Токенизированные TSM, GOOGL, XAU и прочие. Они стоят, пока биржа '
            + 'закрыта, и на открытии дают всплеск ×80 — в крипто-радаре это шум.',
        }),
      ]),
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: 'Сохранить', primary: true,
          onClick: (c) => {
            this.filters = draft;
            settings.set('sx_volradar_filters', draft);
            this.#renderCards();
            c();
          },
        },
      ],
    });
  }

  /** Rândurile pentru fila activă, cu multiplicatorii calculați. */
  #rows() {
    const { minMultiplier, minVolume, maxChange, minRatio, limit } = this.filters;
    let all = market.list().filter((r) => (r.quoteVolume || 0) >= minVolume);
    if (!this.filters.tradfi) all = all.filter((r) => (r.kind || 'crypto') === 'crypto');
    all = all.filter((r) => splitSymbol(r.symbol).quote === 'USDT');
    if (this.onlyMarks) {
      const marks = new Set(settings.get('sx_bookmarks', []) || []);
      all = all.filter((r) => marks.has(r.symbol));
    }

    const cu = all.map((r) => ({
      ...r,
      // Ambii multiplicatori se citesc la fel: „de câte ori peste normalul recent".
      volX: 1 + (Number(r.volTrend) || 0) / 100,
      tradeX: r.tradesTrend == null ? null : 1 + Number(r.tradesTrend) / 100,
    }));

    let rows;
    if (this.tab === 'flow') {
      rows = cu.filter((r) => r.volX >= minMultiplier);
      rows.sort((a, b) => b.volX - a.volX);
      rows.forEach((r) => { r.badge = r.volX; });
    } else if (this.tab === 'trades') {
      const mare = this.tradeMode === 'big';
      rows = cu.filter((r) => {
        if (r.volX < minMultiplier || !r.tradeX || r.tradeX <= 0) return false;
        r.badge = mare ? r.volX / r.tradeX : r.tradeX / r.volX;
        return r.badge >= minRatio;
      });
      rows.sort((a, b) => b.badge - a.badge);
    } else {
      rows = cu.filter((r) =>
        r.volX >= minMultiplier && Math.abs(Number(r.change24h) || 0) <= maxChange);
      rows.sort((a, b) => b.volX - a.volX);
      rows.forEach((r) => { r.badge = r.volX; });
    }

    // „Показано N из M": M sunt monedele cu aflux de oborot chiar acum, nu toate cele
    // care există. Altfel numitorul e o constantă care nu spune nimic.
    const cuAflux = cu.filter((r) => r.volX >= minMultiplier).length;
    return { total: cuAflux, rows: rows.slice(0, limit) };
  }

  #renderCards() {
    if (!this.cardsEl) return;
    const { total, rows } = this.#rows();
    const time = new Date().toLocaleTimeString('ru-RU');
    const inTranzactii = this.tab === 'trades';

    clear(this.metaEl);
    this.metaEl.append(
      el('div', { class: 'radar-updated', text: `обновлено ${time}` }),
      el('div', { class: 'radar-shown', text: `Показано ${rows.length} из ${total}` }),
    );

    if (!rows.length) {
      for (const c of this.cardEls.values()) c.element.remove();
      this.cardEls.clear();
      // Primul tur de sparkline-uri durează ~90 s după pornirea serverului. Până atunci
      // niciun rând n-are `volTrend`, iar „nu e nicio monedă" ar fi o minciună: nu e
      // niciun *răspuns* încă.
      const areDate = market.list().some((r) => r.volTrend != null);
      clear(this.cardsEl);
      this.cardsEl.append(el('div', {
        class: 'radar-empty',
        text: !areDate
          ? 'Радар запускается — первый проход занимает около минуты'
          : inTranzactii
            ? 'Сейчас перекоса между оборотом и числом сделок нет'
            : 'Сейчас нет монет с всплеском оборота',
      }));
      return;
    }
    this.cardsEl.querySelector('.radar-empty')?.remove();

    // Diferență, nu reconstrucție. Agregatele vin la 5 secunde; ştergând tot şi
    // redesenând 60 de carduri cu 60 de SVG-uri, un clic care nimerea între mousedown
    // şi mouseup cădea pe un element deja înlocuit — de aici senzaţia că selectorul
    // „se mută greu". În plus, ordinea se schimba sub cursor.
    const dorite = new Map(rows.map((r) => [r.symbol, r]));
    for (const [sym, card] of this.cardEls) {
      if (!dorite.has(sym)) { card.element.remove(); this.cardEls.delete(sym); }
    }
    for (const [sym, row] of dorite) {
      let card = this.cardEls.get(sym);
      if (!card) { card = this.#card(sym); this.cardEls.set(sym, card); }
      card.refresh(row, inTranzactii);
    }
    for (const sym of dorite.keys()) this.cardsEl.append(this.cardEls.get(sym).element);
  }

  /** Mută doar evidenţierea, fără să atingă restul listei. */
  #syncActive() {
    // Evidenţierea urmează alegerea utilizatorului, nu starea panoului: panoul se
    // aliniază abia după ce s-au încărcat lumânările.
    const ales = this.activ || this.panel?.symbol;
    for (const [sym, card] of this.cardEls) {
      card.element.classList.toggle('active', sym === ales);
    }
  }

  /** Un card, construit o singură dată; conţinutul se împrospătează în loc. */
  #card(symbol) {
    const marcaj = el('button', {
      class: 'radar-card-mark',
      title: 'В закладки',
      onclick: (event) => {
        event.stopPropagation();
        const marks = new Set(settings.get('sx_bookmarks', []) || []);
        if (marks.has(symbol)) marks.delete(symbol); else marks.add(symbol);
        settings.set('sx_bookmarks', [...marks]);
        event.currentTarget.classList.toggle('on', marks.has(symbol));
      },
    }, [icon('bookmark', 11)]);

    const multEl = el('span', { class: 'radar-card-mult' });
    const tagEl = el('div', { class: 'radar-card-tag' }, [
      el('span', { class: 'radar-tag-txt' }),
      el('span', { class: 'radar-tag-help' }, [icon('help', 9)]),
    ]);
    const sparkEl = el('div', { class: 'radar-card-spark' });
    const detailEl = el('div', { class: 'radar-card-detail' });
    const changeEl = el('span', { class: 'radar-card-change' });
    const volEl = el('span', { class: 'radar-card-vol' });

    const element = el('div', {
      class: 'radar-card',
      onclick: () => {
        // Feedback instant. Încărcarea graficului ţine firul principal ocupat ~300 ms
        // (cererea plus aşezarea a o mie de bare în Lightweight Charts), iar în tot acel
        // timp browserul nu apucă să deseneze nimic — nici evidenţierea, nici hover-ul:
        // măsurat, primul cadru după clic venea abia la ~500 ms. Mutăm întâi selecţia,
        // lăsăm un cadru să se deseneze, apoi pornim treaba grea.
        this.activ = symbol;
        this.#syncActive();
        requestAnimationFrame(() => this.panel.setSymbol(symbol));
      },
    }, [
      el('div', { class: 'radar-card-top' }, [
        marcaj,
        iconMoneda(symbol, 18),
        el('span', { class: 'radar-card-ticker', text: baseTicker(symbol) }),
        el('span', { class: 'radar-card-quote', text: splitSymbol(symbol).quote || '' }),
        multEl,
      ]),
      tagEl, sparkEl, detailEl,
      el('div', { class: 'radar-card-bottom' }, [changeEl, volEl]),
    ]);

    const card = {
      element, lumanari: null,
      refresh: (row, inTranzactii) => {
        element.classList.toggle('active', symbol === (this.activ || this.panel?.symbol));
        marcaj.classList.toggle('on', new Set(settings.get('sx_bookmarks', []) || []).has(symbol));
        multEl.textContent = `×${row.badge.toFixed(1)}`;

        tagEl.style.display = inTranzactii ? '' : 'none';
        if (inTranzactii) {
          tagEl.className = `radar-card-tag ${this.tradeMode}`;
          tagEl.querySelector('.radar-tag-txt').textContent =
            this.tradeMode === 'big' ? 'КРУПНЫЙ ИГРОК' : 'ТОЛПА';
          tagEl.querySelector('.radar-tag-help').title = LAMURIRI[this.tradeMode];
        }

        // Lumânările se redesenează doar când chiar vin altele (la 5 minute), nu la
        // fiecare tur de agregate: altfel 60 de SVG-uri s-ar reface din 5 în 5 secunde.
        const date = market.candleSpark(symbol);
        if (date !== card.lumanari) {
          card.lumanari = date;
          clear(sparkEl);
          sparkEl.append(candleSparkline(date, { width: 150, height: 44 }));
        }

        detailEl.style.display = inTranzactii ? '' : 'none';
        if (inTranzactii) {
          detailEl.textContent = `об ×${row.volX.toFixed(1)} · сд ×${row.tradeX.toFixed(1)}`;
        }

        const change = Number(row.change24h) || 0;
        changeEl.className = `radar-card-change ${change >= 0 ? 'up' : 'down'}`;
        changeEl.textContent = fmtPct(change);
        volEl.textContent = `$${fmtVolume(row.quoteVolume, '')}`;
      },
    };
    return card;
  }
}
